import { useEffect, useRef } from "react";
import { clientKey, variantKey } from "./toss/api";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { buildTossCustomerKey } from "./toss/customerKey";

interface UseTossPaymentWidgetArgs {
  userInfo: { phone_num?: string; id?: string | number } | null;
  totalOrderPrice: number;
  isTossSelected: boolean;
}

/**
 * Toss 결제 위젯을 초기화하고 금액 변경 시 자동 업데이트합니다.
 */
export function useTossPaymentWidget({ userInfo, totalOrderPrice, isTossSelected }: UseTossPaymentWidgetArgs) {
  const paymentWidgetRef = useRef<any>(null);
  const paymentMethodsWidgetRef = useRef<any>(null);
  const agreementWidgetRef = useRef<any>(null);

  // 위젯 초기화
  useEffect(() => {
    let isActive = true;

    (async () => {
      if (!isTossSelected) return;
      if (!userInfo?.id) return;
      if (!clientKey) return;
      if (paymentMethodsWidgetRef.current) return;

      try {
        const customerKey = await buildTossCustomerKey(userInfo.id);
        const tossPayments = await loadTossPayments(clientKey);
        const widgets = tossPayments.widgets({ customerKey });

        await widgets.setAmount({ currency: "KRW", value: totalOrderPrice });

        const paymentMethodsWidget = await widgets.renderPaymentMethods({
          selector: "#payment-method",
          variantKey,
        });
        const agreementWidget = await widgets.renderAgreement({
          selector: "#agreement",
          variantKey,
        });

        if (!isActive) {
          await paymentMethodsWidget.destroy();
          await agreementWidget.destroy();
          return;
        }

        if (paymentWidgetRef.current == null) {
          paymentWidgetRef.current = widgets;
        }
        paymentMethodsWidgetRef.current = paymentMethodsWidget;
        agreementWidgetRef.current = agreementWidget;
      } catch (error) {
        console.error("Failed to initialize Toss widgets:", error);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [isTossSelected, totalOrderPrice, userInfo?.id]);

  // 금액 변경 시 위젯 업데이트
  useEffect(() => {
    if (!isTossSelected) return;
    if (paymentWidgetRef.current != null) {
      paymentWidgetRef.current
        .setAmount({ currency: "KRW", value: totalOrderPrice })
        .catch((error: unknown) => console.error("Failed to update Toss widget amount:", error));
    }
  }, [isTossSelected, totalOrderPrice]);

  useEffect(() => {
    return () => {
      const paymentMethodsWidget = paymentMethodsWidgetRef.current;
      const agreementWidget = agreementWidgetRef.current;

      paymentWidgetRef.current = null;
      paymentMethodsWidgetRef.current = null;
      agreementWidgetRef.current = null;

      if (paymentMethodsWidget?.destroy) {
        paymentMethodsWidget.destroy().catch((error: unknown) => {
          console.error("Failed to destroy payment methods widget:", error);
        });
      }
      if (agreementWidget?.destroy) {
        agreementWidget.destroy().catch((error: unknown) => {
          console.error("Failed to destroy agreement widget:", error);
        });
      }
    };
  }, []);

  return { paymentWidgetRef, paymentMethodsWidgetRef, agreementWidgetRef };
}
